import { useEffect, useMemo, useState } from 'react';
import type { Farmer, Season } from '../../../../types';
import { getFieldsByFarmer, getSeasonsByFarmer, getHarvestsByFarmer, getLogsByFarmer } from '../../../../store';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { EmptyState } from '../../../../components/base/EmptyState';
import { formatDate, formatNumber } from '../../../../utils/format';
import { useStore } from '../../../../hooks/useStore';
import { useAuth } from '../../../../hooks/useAuth';
import { getSeasonCompliance } from '../../../../api/schedule';
import type { ScheduleCompliance } from '../../../../api/schedule';
import { AssignCultivationProcessModal } from './AssignCultivationProcessModal';
import { CreateSeasonModal } from './CreateSeasonModal';
import FieldTab from '../../../farmer/info/components/FieldTab';

interface DetailProductionTabProps {
  farmer: Farmer;
  /** Switches the parent sheet's active tab — used by the "Xem tất cả" links
   * below, now that Nhật ký/Sản lượng are their own full tabs instead of a
   * 3-5-entry embed here. */
  onNavigateTab?: (tab: 'diary' | 'harvest') => void;
}

export default function DetailProductionTab({ farmer, onNavigateTab }: DetailProductionTabProps) {
  useStore();
  const { user } = useAuth();
  const fields = getFieldsByFarmer(farmer.id);
  const seasons = getSeasonsByFarmer(farmer.id);
  const [assigningSeason, setAssigningSeason] = useState<Season | null>(null);
  const [creatingSeason, setCreatingSeason] = useState(false);
  // Once a profile HTX pre-created without an account gets synced to a real
  // farmer account, editing that farmer's season (assign crop/cultivation
  // process) moves to the farmer — see EkycService.assertProfileEditAllowed.
  const isSyncLocked = !!farmer.createdWithoutAccount && !!farmer.userId;
  const harvests = getHarvestsByFarmer(farmer.id).filter((h) => !h.isDeleted);
  const diaries = getLogsByFarmer(farmer.id).filter((d) => !d.isDeleted);
  const totalArea = fields.reduce((s, f) => s + f.area, 0);

  const activeSeasons = seasons.filter((s) => s.status === 'Đang canh tác' || s.status === 'Sắp thu hoạch' || s.status === 'Chuẩn bị');
  const recentHarvests = useMemo(() => harvests.filter((h) => h.status !== 'Dự kiến').slice(0, 3), [harvests]);
  const recentDiaries = useMemo(() => diaries.slice(0, 5), [diaries]);

  // Real, system-computed signal (from ScheduleService.reconcileLog cross-referencing
  // each log against the season's planned cultivation calendar) — replaces the old
  // ad-hoc keyword scan, which duplicated (and diverged from) web/src/utils/logParser.ts.
  const warningDiaries = diaries.filter((d) => d.warningLevel && d.warningLevel !== 'none');

  const activeSeasonIds = useMemo(() => activeSeasons.map((s) => s.id).join(','), [activeSeasons]);

  const [complianceBySeasonId, setComplianceBySeasonId] = useState<Record<string, ScheduleCompliance>>({});
  useEffect(() => {
    if (activeSeasonIds === '') {
      setComplianceBySeasonId({});
      return;
    }
    const ids = activeSeasonIds.split(',');
    let cancelled = false;
    Promise.all(ids.map((id) => getSeasonCompliance(id).then((c) => [id, c] as const).catch(() => null)))
      .then((results) => {
        if (cancelled) return;
        setComplianceBySeasonId(Object.fromEntries(results.filter((r): r is readonly [string, ScheduleCompliance] => r !== null)));
      });
    return () => {
      cancelled = true;
    };
  }, [activeSeasonIds]);

  const getFieldName = (id?: string) => fields.find((f) => f.id === id)?.name || '-';

  return (
    <div className="space-y-5">
      {user?.role === 'Hợp tác xã' && farmer.membershipStatus !== 'Đã xác nhận' && (
        <div className="bg-amber-50 rounded-xl border border-amber-100 p-3">
          <p className="text-xs text-amber-800">
            {farmer.membershipStatus === 'Chờ xác nhận'
              ? 'Nông hộ đang chờ HTX xác nhận gia nhập — một số thao tác quản lý (gán quy trình canh tác) sẽ bị khóa cho đến khi xác nhận.'
              : 'Yêu cầu gia nhập của nông hộ đã bị từ chối — thao tác quản lý đang bị khóa.'}
          </p>
        </div>
      )}

      {/* ── Fields: the same screen the farmer uses, with the HTX's rights ── */}
      <FieldTab
        farmerId={farmer.id}
        canEdit={user?.role === 'Hợp tác xã' && farmer.membershipStatus === 'Đã xác nhận' && !isSyncLocked}
      />

      {/* ── Active seasons ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900">Mùa vụ đang quản lý ({activeSeasons.length})</h3>
          {user?.role === 'Hợp tác xã' && farmer.membershipStatus === 'Đã xác nhận' && (
            <button type="button" onClick={() => setCreatingSeason(true)} className="rounded-lg bg-agrida-600 px-3 py-1.5 text-xs font-semibold text-white">
              + Tạo mùa vụ
            </button>
          )}
        </div>
        {activeSeasons.length === 0 ? (
          <EmptyState icon="ri-calendar-line" title="Chưa có mùa vụ" description="Nông hộ chưa tạo mùa vụ nào." />
        ) : (
          <div className="space-y-3">
            {activeSeasons.map((s) => {
              const field = fields.find((f) => f.id === s.fieldId);
              const compliance = complianceBySeasonId[s.id];
              return (
                <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-3 md:p-4">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-sm font-semibold text-slate-900">{s.name}</p>
                    <StatusBadge status={s.status} size="sm" />
                    {compliance && compliance.total > 0 && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          compliance.overdue > 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        Lịch canh tác: {compliance.done}/{compliance.total} đúng hạn{compliance.overdue > 0 ? ` · ${compliance.overdue} quá hạn` : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">Mã: {s.maMuaVu} · {s.cayTrong} ({s.variety}) · {field?.name || '-'}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-600">
                    <span>{formatDate(s.startDate)}{s.endDate ? ` - ${formatDate(s.endDate)}` : ''}</span>
                    <span>Sản lượng dự kiến: {s.expectedYield} {s.unit}</span>
                    {s.dinhMucVatTu && <span>Định mức: {s.dinhMucVatTu}</span>}
                  </div>
                  {user?.role === 'Hợp tác xã' && (
                    farmer.membershipStatus !== 'Đã xác nhận' ? (
                      <p className="mt-2 text-xs text-slate-400">
                        Cần xác nhận nông hộ gia nhập HTX trước khi gán quy trình canh tác.
                      </p>
                    ) : isSyncLocked ? (
                      <p className="mt-2 text-xs text-slate-400">
                        Hồ sơ đã đồng bộ với tài khoản nông hộ — nông hộ tự gán quy trình canh tác cho mùa vụ này.
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAssigningSeason(s)}
                        className="mt-2 text-xs font-medium text-agrida-700 hover:underline"
                      >
                        Gán quy trình canh tác
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Production warnings ── */}
      {warningDiaries.length > 0 && (
        <div className="bg-amber-50 rounded-xl border border-amber-100 p-4">
          <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-alert-line" /></span>
            Cảnh báo sản xuất ({warningDiaries.length})
          </h3>
          <div className="space-y-1.5">
            {warningDiaries.slice(0, 3).map((d) => (
              <p key={d.id} className="text-xs text-amber-700">
                <span className="font-medium">{formatDate(d.ngay)}</span> — {d.hoatDong}
                {d.warningTags && d.warningTags.length > 0 && (
                  <span className="ml-1.5 inline-flex gap-1">
                    {d.warningTags.map((tag) => (
                      <span key={tag} className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">{tag}</span>
                    ))}
                  </span>
                )}
                : {d.chiTiet.length > 80 ? d.chiTiet.slice(0, 80) + '...' : d.chiTiet}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* ── Recent harvest teaser — full list now lives in the "Sản lượng" tab ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900">Thu hoạch gần nhất</h3>
          {harvests.length > 0 && (
            <button type="button" onClick={() => onNavigateTab?.('harvest')} className="text-xs font-semibold text-agrida-700 hover:underline">
              Xem tất cả ({harvests.length}) →
            </button>
          )}
        </div>
        {recentHarvests.length === 0 ? (
          <EmptyState icon="ri-shopping-basket-line" title="Chưa có thu hoạch" description="Nông hộ chưa ghi nhận thu hoạch nào." />
        ) : (
          <div className="space-y-3">
            {recentHarvests.map((h) => {
              // The harvest's own expected quantity — same record, so guaranteed
              // to be in h.donVi. The season's expectedYield carries its own
              // unit and was being blindly multiplied by 1000 (see HarvestCard).
              const duKien = h.soLuong || 0;
              const actual = h.actualYieldConfirmed || h.actualYield || 0;
              const chenh = actual - duKien;

              return (
                <div key={h.id} className="bg-white rounded-xl border border-slate-200 p-3 md:p-4">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-sm font-semibold text-slate-900">{h.maLanThuHoach}</p>
                    <StatusBadge status={h.status} size="sm" />
                    {h.canhBao && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-red-50 text-red-700 border border-red-200">{h.canhBao}</span>}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-xs">
                    <div><span className="text-slate-400">Ngày thu:</span> <span className="font-medium text-slate-700">{h.ngayThuHoach ? formatDate(h.ngayThuHoach) : '—'}</span></div>
                    <div><span className="text-slate-400">Sản lượng:</span> <span className="font-medium text-slate-700">{formatNumber(actual)} {h.donVi}</span></div>
                    <div><span className="text-slate-400">Chênh lệch:</span> <span className={`font-medium ${chenh >= 0 ? 'text-agrida-700' : 'text-red-600'}`}>{duKien > 0 ? `${chenh >= 0 ? '+' : ''}${formatNumber(chenh)} ${h.donVi}` : '—'}</span></div>
                    <div><span className="text-slate-400">Chất lượng:</span> <span className="font-medium text-slate-700">{h.chatLuong || '—'}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Recent diaries teaser — full list now lives in the "Nhật ký" tab ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900">Nhật ký gần đây</h3>
          {diaries.length > 0 && (
            <button type="button" onClick={() => onNavigateTab?.('diary')} className="text-xs font-semibold text-agrida-700 hover:underline">
              Xem tất cả ({diaries.length}) →
            </button>
          )}
        </div>
        {recentDiaries.length === 0 ? (
          <EmptyState icon="ri-book-open-line" title="Chưa có nhật ký" description="Nông hộ chưa ghi nhật ký nào." />
        ) : (
          <div className="space-y-2">
            {recentDiaries.map((d) => (
              <div key={d.id} className="bg-white rounded-xl border border-slate-200 p-3">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <p className="text-xs font-semibold text-slate-900">{formatDate(d.ngay)}</p>
                  <span className="text-xs text-slate-400">{getFieldName(d.fieldId)}</span>
                </div>
                <p className="text-sm text-slate-700">{d.hoatDong}</p>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{d.chiTiet}</p>
                {d.ghiChuHTX && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-agrida-100 text-agrida-700 mt-1">Ghi chú HTX</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {assigningSeason && user?.htxId && (
        <AssignCultivationProcessModal
          season={assigningSeason}
          htxId={user.htxId}
          onClose={() => setAssigningSeason(null)}
        />
      )}

      {creatingSeason && (
        <CreateSeasonModal
          farmerId={farmer.id}
          fields={fields}
          onClose={() => setCreatingSeason(false)}
          onCreated={() => setCreatingSeason(false)}
        />
      )}
    </div>
  );
}